/**
 * MicCheckPanel (LUM-82)
 *
 * Pre-call mic check UI: a live meter while the check listens, and —
 * when it heard nothing — help text, a microphone picker, and a retry.
 * Start stays blocked until a check passes.
 */

import {
  Alert,
  Button,
  List,
  Progress,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import {
  IconMicrophone,
  IconMicrophoneOff,
  IconRefresh,
} from '@tabler/icons-react';
import type { MicCheckStatus } from '@/hooks/useMicCheck';

export interface MicCheckPanelProps {
  status: MicCheckStatus;
  level: number;
  microphones: MediaDeviceInfo[];
  selectedMicrophone: string;
  onSelectMicrophone: (deviceId: string) => void;
  onRetry: () => void;
}

export function MicCheckPanel({
  status,
  level,
  microphones,
  selectedMicrophone,
  onSelectMicrophone,
  onRetry,
}: Readonly<MicCheckPanelProps>) {
  if (status === 'checking') {
    return (
      <Alert
        color="blue"
        variant="light"
        icon={<IconMicrophone size={16} />}
        title="Checking your microphone"
      >
        <Stack gap={8}>
          <Text size="sm">Say “hello” so we can hear you.</Text>
          <Progress
            value={Math.min(100, (level / 60) * 100)}
            size="sm"
            aria-label="Microphone level"
          />
        </Stack>
      </Alert>
    );
  }

  if (status !== 'silent') return null;

  return (
    <Alert
      color="yellow"
      variant="light"
      icon={<IconMicrophoneOff size={16} />}
      title="We couldn't hear your microphone"
    >
      <Stack gap="sm">
        <Text size="sm">
          Your interview can't start until we can hear you. Try this, then check
          again and say a few words:
        </Text>
        <List size="sm" spacing={2}>
          <List.Item>
            Make sure your microphone or headset isn't muted.
          </List.Item>
          {microphones.length > 0 && (
            <List.Item>Pick the microphone you want to use below.</List.Item>
          )}
          <List.Item>
            Allow microphone access for this site in your browser settings.
          </List.Item>
          <List.Item>
            Close other apps that might be using your microphone.
          </List.Item>
        </List>
        {microphones.length > 0 && (
          <Select
            label="Microphone"
            placeholder="Select microphone"
            leftSection={<IconMicrophone size={16} />}
            data={microphones.map(d => ({
              value: d.deviceId,
              label: d.label || `Microphone ${d.deviceId.slice(0, 8)}`,
            }))}
            value={selectedMicrophone}
            onChange={value => value && onSelectMicrophone(value)}
            allowDeselect={false}
            styles={{
              input: { backgroundColor: 'var(--mantine-color-white)' },
            }}
          />
        )}
        <Button
          variant="light"
          size="sm"
          w="fit-content"
          leftSection={<IconRefresh size={16} />}
          onClick={onRetry}
        >
          Check again
        </Button>
      </Stack>
    </Alert>
  );
}
